import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Users, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/**
 * Painel de pré-visualização de destinatários da automação.
 * Recebe `contextKind` + `conditions` + `unitId` e chama
 * `POST /automations/preview` pra mostrar contagem + amostra.
 */
export default function RecipientsPreview({ contextKind, conditions, unitId }) {
  const { apiCall } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    try {
      const r = await apiCall("post", "/automations/preview", {
        context_kind: contextKind || "client_filter",
        conditions: conditions || [],
        unit_id: unitId || null,
        limit_samples: 10,
      });
      setResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao prever destinatários");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-amber-200 bg-amber-50/40 rounded-md p-2 space-y-2" data-testid="recipients-preview">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[11px] font-medium text-amber-900">
          <Users className="h-3 w-3" strokeWidth={1.5} />
          Pré-visualizar destinatários
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 text-[11px]"
          onClick={run}
          disabled={loading}
          data-testid="preview-recipients-btn"
        >
          {loading ? (
            <><Loader2 className="h-3 w-3 mr-1 animate-spin" strokeWidth={1.5} /> Avaliando…</>
          ) : (
            <><RefreshCw className="h-3 w-3 mr-1" strokeWidth={1.5} /> Calcular</>
          )}
        </Button>
      </div>
      {result == null ? (
        <p className="text-[10px] text-muted-foreground">
          Clique em "Calcular" pra ver quantos {contextKind === "invoice" ? "fiados" : contextKind === "appointment" ? "agendamentos" : "clientes"} casam com as condições agora.
        </p>
      ) : (
        <div className="space-y-1">
          <div className="text-xs">
            <span className="font-semibold text-amber-900" data-testid="preview-count">{result.matched}</span>
            <span className="text-muted-foreground"> {contextKind === "invoice" ? "fiado(s)" : contextKind === "appointment" ? "agendamento(s)" : "cliente(s)"} casariam com as condições agora.</span>
          </div>
          {result.samples?.length > 0 && (
            <div className="border border-border rounded bg-background max-h-32 overflow-y-auto">
              <table className="w-full text-[10px]">
                <thead className="bg-secondary/30 sticky top-0">
                  <tr className="text-left text-muted-foreground">
                    <th className="px-1.5 py-0.5">Nome</th>
                    {contextKind === "invoice" && <th className="px-1.5 py-0.5">Em aberto</th>}
                    {contextKind === "appointment" && <th className="px-1.5 py-0.5">Data</th>}
                    {contextKind !== "invoice" && contextKind !== "appointment" && <th className="px-1.5 py-0.5">Funil</th>}
                  </tr>
                </thead>
                <tbody>
                  {result.samples.map((s, i) => (
                    <tr key={i} className="border-t border-border/30" data-testid={`preview-sample-${i}`}>
                      <td className="px-1.5 py-0.5">{s.name}</td>
                      {contextKind === "invoice" && <td className="px-1.5 py-0.5">R$ {Number(s.remaining || 0).toFixed(2)}</td>}
                      {contextKind === "appointment" && <td className="px-1.5 py-0.5 text-muted-foreground">{(s.date || "").substring(0, 16)}</td>}
                      {contextKind !== "invoice" && contextKind !== "appointment" && <td className="px-1.5 py-0.5">{s.funnel_status || "—"}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {result.matched > (result.samples?.length || 0) && (
            <p className="text-[10px] text-muted-foreground italic">
              ...e mais {result.matched - (result.samples?.length || 0)} não exibido(s).
            </p>
          )}
        </div>
      )}
    </div>
  );
}
